import { onError } from '@apollo/client/link/error';
import { toast } from 'sonner';
import tokenManager from '../tokenManager';

// Track if we've already shown a network error to avoid spam
let lastNetworkErrorTime = 0;
const NETWORK_ERROR_COOLDOWN = 5000; // 5 seconds

/**
 * Error link that handles GraphQL and network errors with user-friendly notifications.
 * - Shows error messages to users
 * - Handles authentication errors with redirect
 * - Prevents error message spam
 */
const errorLink = onError((errorResponse: any) => {
  const { graphQLErrors, networkError, operation } = errorResponse;

  console.log('[ErrorLink] Error detected:', {
    hasGraphQLErrors: !!graphQLErrors,
    hasNetworkError: !!networkError,
    operation: operation.operationName,
  });

  // Handle GraphQL errors
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      console.log('[ErrorLink] GraphQL error:', {
        message: err.message,
        code: err.extensions?.code,
        extensions: err.extensions,
      });

      // Skip known backend issues
      if (err.message?.includes("Cannot return null for non-nullable field")) {
        console.warn(`GraphQL field error (backend issue):`, err.message);
        continue;
      }

      // Detect authentication errors
      const isAuthError =
        err.extensions?.code === 'UNAUTHENTICATED' ||
        err.message?.toLowerCase().includes('authentication') ||
        err.message?.toLowerCase().includes('unauthorized') ||
        err.message?.toLowerCase().includes('token expired') ||
        err.message?.toLowerCase().includes('invalid token');

      if (isAuthError) {
        console.error('[ErrorLink] Authentication error detected - redirecting to login');
        toast.warning('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        tokenManager.clearTokens();
        if (typeof window !== 'undefined') {
          setTimeout(() => {
            window.location.href = '/login';
          }, 1000);
        }
        return; // Stop processing other errors
      }

      // Show user-friendly error message for other GraphQL errors
      const errorMessage = err.message || 'Đã xảy ra lỗi. Vui lòng thử lại.';
      toast.error(errorMessage, { duration: 4000 });
      console.error('[ErrorLink] GraphQL error:', err);
    }
  }

  // Handle network errors (HTTP status codes)
  if (networkError) {
    console.log('[ErrorLink] Network error:', {
      statusCode: 'statusCode' in networkError ? networkError.statusCode : 'N/A',
      message: networkError.message,
    });

    // Handle 401 Unauthorized
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      console.error('[ErrorLink] 401 Unauthorized - redirecting to login');
      toast.warning('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
      tokenManager.clearTokens();
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
      }
      return;
    }

    // Handle other network errors with rate limiting to prevent spam
    const now = Date.now();
    if (now - lastNetworkErrorTime > NETWORK_ERROR_COOLDOWN) {
      lastNetworkErrorTime = now;

      // Determine error message based on error type
      let errorMessage = 'Không thể kết nối đến máy chủ.';

      if (networkError.message?.toLowerCase().includes('timeout')) {
        errorMessage = 'Yêu cầu quá thời gian chờ. Vui lòng thử lại.';
      } else if (networkError.message?.toLowerCase().includes('network')) {
        errorMessage = 'Lỗi kết nối mạng. Vui lòng kiểm tra kết nối internet.';
      } else if ('statusCode' in networkError) {
        const statusCode = (networkError as any).statusCode;
        if (statusCode >= 500) {
          errorMessage = 'Máy chủ đang gặp sự cố. Vui lòng thử lại sau.';
        } else if (statusCode >= 400) {
          errorMessage = 'Yêu cầu không hợp lệ. Vui lòng thử lại.';
        }
      }

      toast.error(errorMessage, { duration: 5000 });
    }

    console.error('[ErrorLink] Network error:', networkError);
  }

  // Handle case where error exists but no graphQLErrors or networkError (e.g., AbortError from timeout)
  if (!graphQLErrors && !networkError && operation) {
    console.log('[ErrorLink] Unknown error type detected - possibly AbortError');
    const now = Date.now();
    if (now - lastNetworkErrorTime > NETWORK_ERROR_COOLDOWN) {
      lastNetworkErrorTime = now;
      toast.error('Yêu cầu quá thời gian chờ. Vui lòng thử lại.', { duration: 5000 });
    }
  }
});

export default errorLink;